import type shaka from 'shaka-player';

export type SidecarCaptionTrack = {
  uri: string;
  language: string;
  kind: 'captions' | 'subtitles';
  label: string;
};

/** Empty a11y placeholder from CustomPlayer when the API lists no caption file. */
export function isPlaceholderCaptionTrackSrc(src: string): boolean {
  return src.startsWith('data:text/vtt') && src.includes(encodeURIComponent('WEBVTT\n\n'));
}

export function resolveCaptionUri(src: string): string {
  try {
    return new URL(src, typeof window !== 'undefined' ? window.location.href : src).href;
  } catch {
    return src;
  }
}

function sidecarKey(track: SidecarCaptionTrack): string {
  return `${track.kind}|${track.language}|${track.label}|${resolveCaptionUri(track.uri)}`;
}

export function sidecarFromTrackElement(element: HTMLTrackElement): SidecarCaptionTrack | null {
  if (element.kind !== 'captions' && element.kind !== 'subtitles') return null;
  if (!element.src || isPlaceholderCaptionTrackSrc(element.src)) return null;
  return {
    uri: element.src,
    language: element.srclang || 'en',
    kind: element.kind === 'captions' ? 'captions' : 'subtitles',
    label: element.label || element.srclang || 'Subtitles',
  };
}

export function collectSidecarCaptionsFromVideo(video: HTMLVideoElement): SidecarCaptionTrack[] {
  const tracks: SidecarCaptionTrack[] = [];
  for (const node of video.querySelectorAll('track')) {
    if (!(node instanceof HTMLTrackElement)) continue;
    const sidecar = sidecarFromTrackElement(node);
    if (sidecar) tracks.push(sidecar);
  }
  return tracks;
}

export function mergeSidecarCaptionTracks(
  fromConfig: readonly SidecarCaptionTrack[] | undefined,
  fromVideo: readonly SidecarCaptionTrack[],
): SidecarCaptionTrack[] {
  const merged = new Map<string, SidecarCaptionTrack>();
  for (const track of [...(fromConfig ?? []), ...fromVideo]) {
    if (!track.uri || isPlaceholderCaptionTrackSrc(track.uri)) continue;
    merged.set(sidecarKey(track), track);
  }
  return Array.from(merged.values());
}

export function findShakaCaptionTrack(
  player: shaka.Player,
  sidecar: SidecarCaptionTrack,
): shaka.extern.Track | null {
  return (
    player.getTextTracks().find((track) => {
      if (track.label !== sidecar.label) return false;
      if (track.language !== sidecar.language) return false;
      if ((track.kind ?? 'subtitles') !== sidecar.kind) return false;
      return true;
    }) ?? null
  );
}

function disableNativeCaptionTracks(video: HTMLVideoElement): void {
  for (const track of Array.from(video.textTracks)) {
    if (track.kind === 'captions' || track.kind === 'subtitles') {
      track.mode = 'disabled';
    }
  }
}

/** Prefetch sidecar VTT so CORS / relative URLs do not break Shaka's text pipeline. */
async function resolveSidecarCaptionLoadUri(uri: string): Promise<string> {
  const absolute = resolveCaptionUri(uri);
  if (absolute.startsWith('blob:') || absolute.startsWith('data:')) {
    return absolute;
  }

  try {
    const response = await fetch(absolute);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const vtt = await response.text();
    return URL.createObjectURL(new Blob([vtt], { type: 'text/vtt' }));
  } catch (error) {
    console.warn('[player-sdk] Caption VTT prefetch failed, trying direct URL:', absolute, error);
    return absolute;
  }
}

export async function registerSidecarCaptionWithShaka(
  player: shaka.Player,
  sidecar: SidecarCaptionTrack,
): Promise<shaka.extern.Track | null> {
  const existing = findShakaCaptionTrack(player, sidecar);
  if (existing) return existing;

  const loadUri = await resolveSidecarCaptionLoadUri(sidecar.uri);
  try {
    const track = await player.addTextTrackAsync(
      loadUri,
      sidecar.language,
      sidecar.kind,
      'text/vtt',
      '',
      sidecar.label,
    );
    return track;
  } catch (error) {
    console.warn('[player-sdk] Failed to load sidecar captions:', sidecar.label, loadUri, error);
    return null;
  }
}

export async function addSidecarCaptionTracks(
  player: shaka.Player,
  tracks: readonly SidecarCaptionTrack[],
  isLiveStream: boolean,
  video?: HTMLVideoElement,
): Promise<void> {
  if (isLiveStream || tracks.length === 0) return;

  player.configure('streaming.alwaysStreamText', true);

  for (const track of tracks) {
    await registerSidecarCaptionWithShaka(player, track);
  }

  if (video) {
    disableNativeCaptionTracks(video);
  }
}

export async function activateShakaCaptionTrack(
  player: shaka.Player,
  video: HTMLVideoElement,
  track: shaka.extern.Track,
): Promise<void> {
  disableNativeCaptionTracks(video);
  await player.selectTextTrack(track);
  await player.setTextTrackVisibility(true);
}

export async function disableShakaCaptions(player: shaka.Player, video: HTMLVideoElement): Promise<void> {
  disableNativeCaptionTracks(video);
  await player.setTextTrackVisibility(false);
}
